import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Save, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
    ufficio: "Ufficio Produzione",
    operatore: "Operatore Saldatura",
    operatore_estrusione: "Operatore Estrusione",
    magazziniere: "Magazziniere",
    segreteria: "Segreteria",
};
const ROLE_TONE = {
    ufficio: "text-amber-300 border-amber-500/40 bg-amber-500/5",
    operatore: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    operatore_estrusione: "text-sky-300 border-sky-500/40 bg-sky-500/5",
    magazziniere: "text-blue-300 border-blue-500/40 bg-blue-500/5",
    segreteria: "text-violet-300 border-violet-500/40 bg-violet-500/5",
};

export default function Permessi({ embedded = false }) {
    const { refreshPerms } = useAuth();
    const [defs, setDefs] = useState([]);
    const [roles, setRoles] = useState([]);
    const [perms, setPerms] = useState({}); // {role: {key: bool}}
    const [dirty, setDirty] = useState({}); // {role: bool}
    const [loading, setLoading] = useState(true);
    const [busyRole, setBusyRole] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: d }, { data: p }] = await Promise.all([
                api.get("/permissions/defs"), api.get("/permissions")
            ]);
            setDefs(d.defs); setRoles(d.roles); setPerms(p); setDirty({});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Raggruppa per sezione
    const grouped = useMemo(() => {
        const g = {};
        for (const d of defs) {
            if (!g[d.section]) g[d.section] = [];
            g[d.section].push(d);
        }
        return g;
    }, [defs]);

    const toggle = (role, key) => {
        setPerms(p => ({ ...p, [role]: { ...(p[role] || {}), [key]: !((p[role] || {})[key]) } }));
        setDirty(d => ({ ...d, [role]: true }));
    };

    const save = async (role) => {
        setBusyRole(role);
        try {
            await api.put(`/permissions/${role}`, { perms: perms[role] || {} });
            setDirty(d => ({ ...d, [role]: false }));
            toast.success(`Permessi salvati per ${ROLE_LABEL[role]}`);
            if (refreshPerms) refreshPerms();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyRole(null); }
    };

    return (
        <div className="space-y-6">
            {!embedded && (
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-red-400/80">ADMIN</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Permessi per ruolo</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">Decidi quali schede vede ogni ruolo e cosa può fare. L'<span className="text-red-300 font-mono">admin</span> ha sempre tutti i permessi e non compare in questa tabella. Le modifiche non salvate hanno il bordo ambra.</p>
                </div>
                <Button onClick={load} variant="outline" className="bg-transparent border-slate-700 gap-2" data-testid="button-reload-perms">
                    <RotateCcw size={14} /> Ricarica
                </Button>
            </div>
            )}
            {embedded && (
                <div className="flex justify-end">
                    <Button onClick={load} variant="outline" className="bg-transparent border-slate-700 gap-2" data-testid="button-reload-perms">
                        <RotateCcw size={14} /> Ricarica
                    </Button>
                </div>
            )}

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800 sticky top-0 bg-slate-950/95 backdrop-blur">
                            <tr>
                                <th className="text-left px-4 py-3 min-w-[280px]">Scheda / Azione</th>
                                {roles.map(r => (
                                    <th key={r} className={`text-center px-3 py-3 w-32 border-l border-slate-800/60 ${ROLE_TONE[r].split(' ')[0]}`}>
                                        <div className="font-semibold">{ROLE_LABEL[r] || r}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody data-testid="table-perms">
                            {loading && <tr><td colSpan={roles.length + 1} className="px-4 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && Object.entries(grouped).map(([section, items]) => (
                                <>
                                    <tr key={`sec-${section}`} className="bg-slate-900/70 border-b border-slate-800">
                                        <td colSpan={roles.length + 1} className="px-4 py-2 text-[11px] uppercase tracking-widest font-mono text-slate-400">
                                            {section}
                                        </td>
                                    </tr>
                                    {items.map(def => (
                                        <tr key={def.key} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`row-perm-${def.key}`}>
                                            <td className="px-4 py-2.5 text-slate-300">
                                                {def.action}
                                                <span className="ml-2 text-[10px] font-mono text-slate-600">{def.key}</span>
                                            </td>
                                            {roles.map(role => {
                                                const on = !!(perms[role] || {})[def.key];
                                                return (
                                                    <td key={role} className={`text-center px-3 py-2.5 border-l border-slate-800/60 ${dirty[role] ? "bg-amber-500/5" : ""}`}>
                                                        <button
                                                            onClick={() => toggle(role, def.key)}
                                                            data-testid={`toggle-${role}-${def.key}`}
                                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition ${
                                                                on
                                                                    ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/25"
                                                                    : "bg-slate-900 border-slate-700 text-slate-600 hover:bg-slate-800"
                                                            }`}
                                                            title={on ? "Attivo — clicca per disattivare" : "Disattivato — clicca per attivare"}
                                                        >
                                                            {on ? "✓" : "—"}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </>
                            ))}
                        </tbody>
                        {!loading && (
                            <tfoot className="sticky bottom-0 bg-slate-950/95 backdrop-blur border-t border-slate-800">
                                <tr>
                                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">Salva per ruolo</td>
                                    {roles.map(role => (
                                        <td key={role} className="px-3 py-3 text-center border-l border-slate-800/60">
                                            <Button
                                                onClick={() => save(role)}
                                                disabled={!dirty[role] || busyRole === role}
                                                data-testid={`button-save-${role}`}
                                                className={`h-8 text-xs gap-1 ${dirty[role] ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-500"}`}
                                            >
                                                <Save size={12} /> Salva
                                            </Button>
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </Card>

            <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                <ShieldCheck size={12} /> Le modifiche hanno effetto al prossimo refresh dell'utente interessato (o dopo logout/login).
            </div>
        </div>
    );
}
